import { Cached, Delete, PlayArrow } from '@mui/icons-material';
import { Chip } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { useState } from 'react';

import {
  EVENT_RETRY_STATUS,
  EventFailureDTO
} from '../../../services/Hub/notificationCenter/types';
import { getStatusColor } from './failed-events.utils';

type Props = {
  organizations: any[];
  eventFailures: EventFailureDTO[];
  fetchNextPage: (model: { page: number; pageSize: number }) => Promise<void>;
  isFetching: boolean;
  totalRows: number;
  retryEvent: (id: string) => Promise<void>;
  ignoreEvent: (id: string) => Promise<void>;
  updateEvent: (id: string) => Promise<void>;
};

export const FailedEventsView = (props: Props) => {
  const {
    eventFailures,
    fetchNextPage,
    isFetching,
    totalRows,
    organizations,
    retryEvent,
    ignoreEvent,
    updateEvent
  } = props;

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20
  });

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'id',
      flex: 1,
      minWidth: 100
    },
    {
      field: 'organizationId',
      disableReorder: true,
      headerName: 'Organização',
      width: 100,
      valueGetter: value => {
        return organizations?.find(o => o.id === value)?.fantasyName;
      },
      renderCell: ({ value }) => <Chip label={value} color={'secondary'} size="small" />
    },
    {
      field: 'source',
      headerName: 'Fonte',
      flex: 2,
      minWidth: 150,
      valueGetter: (value, row) => value + ':' + row.detailType,
      renderCell: ({ value }) => {
        return <Chip label={value} size="small" variant="outlined" />;
      }
    },

    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: ({ row }) => (
        <Chip label={row.status} color={getStatusColor(row.status)} size="small" />
      )
    },

    {
      field: 'createdAt',
      headerName: 'data do erro',
      width: 150,
      renderCell: ({ row }) => (
        <div>
          {new Date(row.createdAt).toLocaleDateString('pt-BR')}{' '}
          {new Date(row.createdAt).toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      )
    },
    {
      field: 'lastAttemptAt',
      headerName: 'Retry em',
      width: 150,
      renderCell: ({ row }) => {
        if (!row.lastAttemptAt) return <></>;
        return (
          <div>
            {new Date(row.lastAttemptAt).toLocaleDateString('pt-BR')}{' '}
            {new Date(row.lastAttemptAt).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        );
      }
    },
    {
      field: 'attemptCount',
      headerName: 'n° tentativas',
      width: 100
    },
    {
      field: 'actions',
      headerName: 'Ações',
      type: 'actions',
      width: 120,
      getActions: ({ row }) => {
        if (row.status === EVENT_RETRY_STATUS.PENDING) {
          return [
            <GridActionsCellItem
              icon={<Cached />}
              key="update"
              label="Atualizar"
              color="primary"
              onClick={() => updateEvent(row.id)}
            />
          ];
        }
        if (row.status === EVENT_RETRY_STATUS.FAILED) {
          return [
            <GridActionsCellItem
              icon={<Delete />}
              key="delete"
              label="Ignorar evento"
              color="default"
              onClick={() => ignoreEvent(row.id)}
            />,
            <GridActionsCellItem
              icon={<Cached />}
              key="update"
              label="Atualiazar"
              color="primary"
              onClick={() => updateEvent(row.id)}
            />,
            <GridActionsCellItem
              icon={<PlayArrow />}
              key="retry"
              label="Retry"
              color="primary"
              onClick={() => retryEvent(row.id)}
            />
          ];
        }
        return [];
      }
    }
  ];

  const handlePaginationChange = async (model: typeof paginationModel) => {
    setPaginationModel(model);
    await fetchNextPage(model);
  };

  return (
    <DataGrid
      rows={eventFailures}
      columns={columns}
      rowCount={totalRows}
      paginationMode="server"
      loading={isFetching}
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationChange}
      pageSizeOptions={[20]}
      disableColumnSorting
      disableColumnFilter
      autoHeight
    />
  );
};
